import {createAsyncThunk} from '@reduxjs/toolkit'
import axios, {AxiosError} from 'axios'
import {setupAxios} from '../../setup-axios'
import {
  inventoryTypesUrl,
  createInventoryTypeUrl,
  updateInventoryTypeUrl,
  deleteInventoryTypeUrl,
  activateInventoryTypeUrl,
  suspendInventoryTypeUrl,
} from '../../api'
import {
  FetchInventoryTypesRequest,
  FetchAllInventoryTypesRequest,
  FetchInventoryTypeByIdRequest,
  CreateInventoryTypeRequest,
  UpdateInventoryTypeRequest,
  DeleteInventoryTypeRequest,
  ActivateInventoryTypeRequest,
  SuspendInventoryTypeRequest,
  FetchInventoryTypesResponse,
  FetchInventoryTypeByIdResponse,
  CreateInventoryTypeResponse,
  UpdateInventoryTypeResponse,
  DeleteInventoryTypeResponse,
  ActivateInventoryTypeResponse,
  SuspendInventoryTypeResponse,
} from './types'
import {ResponseError} from '../commonTypes'

setupAxios(axios)

export const fetchInventoryTypes = createAsyncThunk<
  FetchInventoryTypesResponse,
  FetchInventoryTypesRequest,
  {rejectValue: ResponseError}
>('inventoryType/fetchInventoryTypes', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.get(inventoryTypesUrl, {
      params: {
        search: params.search,
        category: params.category,
        equipmentStatus: params.equipmentStatus,
        pageNumber: params.pageNumber,
        pageCount: params.pageCount,
        sortField: params.sortField,
        sortOrder: params.sortOrder,
        establishement: params.establishement,
      },
    })
    return response.data
  } catch (error: any) {
    console.error('fetchInventoryTypes API error:', error)
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to fetch inventory types'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const fetchAllInventoryTypes = createAsyncThunk<
  FetchInventoryTypesResponse,
  FetchAllInventoryTypesRequest,
  {rejectValue: ResponseError}
>('inventoryType/fetchAllInventoryTypes', async (params, {rejectWithValue}) => {
  try {
    const url = `${inventoryTypesUrl}/all`
    const response = await axios.get(url, {
      params: {
        establishement: params.establishement,
      },
    })
    return response.data
  } catch (error: any) {
    console.error('fetchAllInventoryTypes API error:', error)
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to fetch all inventory types'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const fetchInventoryTypeById = createAsyncThunk<
  FetchInventoryTypeByIdResponse,
  FetchInventoryTypeByIdRequest,
  {rejectValue: ResponseError}
>('inventoryType/fetchInventoryTypeById', async (params, {rejectWithValue}) => {
  try {
    const url = `${inventoryTypesUrl}/${params.id}`
    const response = await axios.get(url)
    return response.data
  } catch (error: any) {
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to fetch inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const createInventoryType = createAsyncThunk<
  CreateInventoryTypeResponse,
  CreateInventoryTypeRequest,
  {rejectValue: ResponseError}
>('inventoryType/createInventoryType', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.post(createInventoryTypeUrl, params)
    return response.data
  } catch (error: any) {
    console.error('createInventoryType API error:', error)
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to create inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const updateInventoryType = createAsyncThunk<
  UpdateInventoryTypeResponse,
  UpdateInventoryTypeRequest,
  {rejectValue: ResponseError}
>('inventoryType/updateInventoryType', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.post(updateInventoryTypeUrl, params)
    return response.data
  } catch (error: any) {
    console.error('updateInventoryType API error:', error)
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to update inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const deleteInventoryType = createAsyncThunk<
  DeleteInventoryTypeResponse,
  DeleteInventoryTypeRequest,
  {rejectValue: ResponseError}
>('inventoryType/deleteInventoryType', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.post(deleteInventoryTypeUrl, params)
    return response.data
  } catch (error: any) {
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to delete inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const activateInventoryType = createAsyncThunk<
  ActivateInventoryTypeResponse,
  ActivateInventoryTypeRequest,
  {rejectValue: ResponseError}
>('inventoryType/activateInventoryType', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.post(activateInventoryTypeUrl, params)
    return response.data
  } catch (error: any) {
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to activate inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})

export const suspendInventoryType = createAsyncThunk<
  SuspendInventoryTypeResponse,
  SuspendInventoryTypeRequest,
  {rejectValue: ResponseError}
>('inventoryType/suspendInventoryType', async (params, {rejectWithValue}) => {
  try {
    const response = await axios.post(suspendInventoryTypeUrl, params)
    return response.data
  } catch (error: any) {
    const err: any = error as AxiosError
    const errorMessage = err.response?.data?.message || 'Failed to suspend inventory type'
    return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
  }
})
